import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  CheckSquare,
  FileText,
  CalendarDays,
  AlertTriangle,
  Eye,
  type LucideIcon,
} from 'lucide-react'
import { calendarApi, apiClient } from '@/services/api'
import { cn } from '@/lib/utils'
import { toast } from '@/lib/toast'
import { ContentDetailModal } from '@/components/ped/ContentDetailModal'
import type { Content } from '@/types'

interface CalendarEvent {
  id: string
  title: string
  date: string
  type: 'task' | 'content' | 'event' | 'deadline'
  status?: string
  client?: string
  project?: string
  allDay?: boolean
}

const TYPE_COLORS: Record<CalendarEvent['type'], string> = {
  task: 'bg-accent-100 text-accent-700 border-accent-200',
  content: 'bg-success-50 text-success-700 border-success-200',
  event: 'bg-purple-100 text-purple-700 border-purple-200',
  deadline: 'bg-danger-50 text-danger-700 border-danger-200',
}

// Colore evento in base alla vicinanza temporale
function getEventTimeColor(eventDate: string): string {
  const now = new Date()
  const evDate = new Date(eventDate)
  const diffMs = evDate.getTime() - now.getTime()
  const diffHours = diffMs / (1000 * 60 * 60)
  const diffDays = diffMs / (1000 * 60 * 60 * 24)

  // Passato → grigio
  if (diffHours < -2) return 'bg-slate-100 text-slate-500'
  // Oggi / nelle prossime 2 ore → rosso (urgente)
  if (diffHours >= -2 && diffHours <= 2) return 'bg-red-500 text-white'
  // Domani (entro 24h) → arancione
  if (diffDays <= 1) return 'bg-amber-100 text-amber-800'
  // Entro 3 giorni → giallo
  if (diffDays <= 3) return 'bg-yellow-100 text-yellow-800'
  // Entro 7 giorni → azzurro
  if (diffDays <= 7) return 'bg-blue-50 text-blue-700'
  // Lontano → verde chiaro
  return 'bg-emerald-50 text-emerald-700'
}

const TYPE_ICONS: Record<CalendarEvent['type'], LucideIcon> = {
  task: CheckSquare,
  content: FileText,
  event: CalendarDays,
  deadline: CalendarIcon,
}

const TYPE_LABELS: Record<CalendarEvent['type'], string> = {
  task: 'Task',
  content: 'Contenuto',
  event: 'Evento',
  deadline: 'Scadenza',
}

// Tipi visibili nella legenda (task escluso dal calendario social)
const LEGEND_TYPES: CalendarEvent['type'][] = ['content', 'event', 'deadline']

// Palette colori per clienti (bg, text, border)
const CLIENT_PALETTE = [
  'bg-blue-100 text-blue-800 border-blue-200',
  'bg-emerald-100 text-emerald-800 border-emerald-200',
  'bg-orange-100 text-orange-800 border-orange-200',
  'bg-pink-100 text-pink-800 border-pink-200',
  'bg-violet-100 text-violet-800 border-violet-200',
  'bg-cyan-100 text-cyan-800 border-cyan-200',
  'bg-amber-100 text-amber-800 border-amber-200',
  'bg-rose-100 text-rose-800 border-rose-200',
  'bg-teal-100 text-teal-800 border-teal-200',
  'bg-indigo-100 text-indigo-800 border-indigo-200',
  'bg-lime-100 text-lime-800 border-lime-200',
  'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200',
  'bg-sky-100 text-sky-800 border-sky-200',
  'bg-red-100 text-red-800 border-red-200',
  'bg-yellow-100 text-yellow-800 border-yellow-200',
]

// Colori dot per la legenda progetti
const PROJECT_DOT_PALETTE = [
  'bg-blue-400',
  'bg-emerald-400',
  'bg-orange-400',
  'bg-pink-400',
  'bg-violet-400',
  'bg-cyan-400',
  'bg-amber-400',
  'bg-rose-400',
  'bg-teal-400',
  'bg-indigo-400',
  'bg-lime-400',
  'bg-fuchsia-400',
  'bg-sky-400',
  'bg-red-400',
  'bg-yellow-400',
]

function getProjectColorIndex(name: string): number {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return Math.abs(hash) % CLIENT_PALETTE.length
}

function getProjectColor(projectName: string | undefined): string {
  if (!projectName) return 'bg-slate-100 text-slate-600 border-slate-200'
  return CLIENT_PALETTE[getProjectColorIndex(projectName)] ?? 'bg-slate-100 text-slate-600 border-slate-200'
}

function getProjectDotColor(projectName: string): string {
  return PROJECT_DOT_PALETTE[getProjectColorIndex(projectName)] ?? 'bg-slate-400'
}

const DAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']
const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
]

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

function getFirstDayOfMonth(year: number, month: number): number {
  // 0=Sun -> convert to Mon-based (Mon=0, ..., Sun=6)
  const day = new Date(year, month, 1).getDay()
  return day === 0 ? 6 : day - 1
}

function formatDateParam(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export function CalendarPage() {
  const queryClient = useQueryClient()
  const today = new Date()
  const [currentYear, setCurrentYear] = useState(today.getFullYear())
  const [currentMonth, setCurrentMonth] = useState(today.getMonth())
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [filterReview, setFilterReview] = useState(false)
  const [selectedContent, setSelectedContent] = useState<Content | null>(null)
  const [loadingContentId, setLoadingContentId] = useState<string | null>(null)

  async function openContent(contentId: string) {
    setLoadingContentId(contentId)
    try {
      const res = await apiClient.get<any>(`/ped/contents/${contentId}`)
      const content = (res as any)?.data ?? res
      if (content?.id) {
        setSelectedContent(content)
      } else {
        toast.error('Contenuto non trovato')
      }
    } catch {
      toast.error('Impossibile aprire il contenuto')
    } finally {
      setLoadingContentId(null)
    }
  }

  // Build start/end date for the current month view
  const startDate = formatDateParam(currentYear, currentMonth, 1)
  const lastDay = getDaysInMonth(currentYear, currentMonth)
  const endDate = formatDateParam(currentYear, currentMonth, lastDay)

  const { data: eventsRaw, isLoading, isError } = useQuery<CalendarEvent[]>({
    queryKey: ['calendar-events', currentYear, currentMonth],
    queryFn: async () => {
      const result = await calendarApi.getEvents({ startDate, endDate })
      // Handle both wrapped { data: [...] } and raw array responses
      const raw = (result as any)?.data
      if (Array.isArray(raw)) return raw
      if (raw?.data && Array.isArray(raw.data)) return raw.data
      if (Array.isArray(result)) return result as CalendarEvent[]
      return []
    },
    staleTime: 60_000,
    retry: 1,
  })

  const allEventsRaw: CalendarEvent[] = eventsRaw ?? []
  const reviewCount = allEventsRaw.filter((e) => e.type === 'content' && (e.status === 'IN_REVIEW' || e.status === 'CHANGES_REQUESTED')).length
  const allEvents = filterReview
    ? allEventsRaw.filter((e) => e.type === 'content' && (e.status === 'IN_REVIEW' || e.status === 'CHANGES_REQUESTED'))
    : allEventsRaw

  // Estrai progetti unici per la legenda
  const uniqueProjects = [...new Set(allEvents.map((e) => e.project).filter(Boolean))] as string[]

  function prevMonth() {
    if (currentMonth === 0) {
      setCurrentMonth(11)
      setCurrentYear((y) => y - 1)
    } else {
      setCurrentMonth((m) => m - 1)
    }
    setSelectedDate(null)
  }

  function nextMonth() {
    if (currentMonth === 11) {
      setCurrentMonth(0)
      setCurrentYear((y) => y + 1)
    } else {
      setCurrentMonth((m) => m + 1)
    }
    setSelectedDate(null)
  }

  const daysInMonth = getDaysInMonth(currentYear, currentMonth)
  const firstDayOffset = getFirstDayOfMonth(currentYear, currentMonth)

  // Build grid cells (some empty for offset)
  const cells: (number | null)[] = [
    ...Array.from({ length: firstDayOffset }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ]

  // Pad to complete the last row
  while (cells.length % 7 !== 0) cells.push(null)

  function getDateString(day: number): string {
    return formatDateParam(currentYear, currentMonth, day)
  }

  function getEventsForDay(day: number): CalendarEvent[] {
    const dateStr = getDateString(day)
    return allEvents.filter((e) => e.date.startsWith(dateStr))
  }

  const selectedDateEvents = selectedDate
    ? allEvents.filter((e) => e.date.startsWith(selectedDate))
    : []

  const isToday = (day: number) => {
    return (
      day === today.getDate() &&
      currentMonth === today.getMonth() &&
      currentYear === today.getFullYear()
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Calendario</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Contenuti, eventi e scadenze
          </p>
        </div>
        <button
          type="button"
          onClick={() => setFilterReview(!filterReview)}
          aria-pressed={filterReview}
          className={cn(
            'inline-flex items-center justify-center gap-2 w-full sm:w-auto px-3.5 py-2 text-sm font-medium rounded-full border transition-colors',
            filterReview
              ? 'bg-warning-50 text-warning-700 border-warning-200 hover:bg-warning-100'
              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
          )}
        >
          <Eye className="w-4 h-4" />
          In Revisione
          {reviewCount > 0 && (
            <span className={cn(
              'text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center tabular-nums',
              filterReview ? 'bg-warning-100 text-warning-700' : 'bg-slate-100 text-slate-600'
            )}>
              {reviewCount}
            </span>
          )}
        </button>
      </div>

      {/* Legenda temporale */}
      <div className="flex items-center gap-x-4 gap-y-2 flex-wrap">
        {[
          { color: 'bg-red-500', label: 'Ora / Imminente' },
          { color: 'bg-amber-400', label: 'Domani' },
          { color: 'bg-yellow-300', label: 'Entro 3 giorni' },
          { color: 'bg-blue-400', label: 'Entro 7 giorni' },
          { color: 'bg-emerald-400', label: 'Oltre 7 giorni' },
          { color: 'bg-slate-300', label: 'Passato' },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-1.5">
            <span className={cn('w-2 h-2 rounded-full flex-shrink-0', item.color)} />
            <span className="text-xs text-slate-500">{item.label}</span>
          </div>
        ))}
      </div>

      {isError && (
        <div className="flex items-center gap-2 p-3 bg-warning-50 border border-warning-200 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-warning-600 flex-shrink-0" />
          <p className="text-xs text-warning-700">
            Impossibile caricare gli eventi dal server. Verificare la connessione.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_280px] gap-6">
        {/* Calendar grid */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden">
          {/* Month navigation */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
            <h2 className="text-sm font-semibold text-slate-900 tabular-nums">
              {MONTHS[currentMonth]} {currentYear}
            </h2>
            <div className="flex items-center gap-1">
              <button
                onClick={prevMonth}
                className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setCurrentYear(today.getFullYear())
                  setCurrentMonth(today.getMonth())
                  setSelectedDate(null)
                }}
                className="px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
              >
                Oggi
              </button>
              <button
                onClick={nextMonth}
                className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Horizontal scroll wrapper: keeps columns readable on mobile */}
          <div className="overflow-x-auto">
            <div className="min-w-[640px]">
              {/* Day headers */}
              <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
                {DAYS.map((day) => (
                  <div key={day} className="px-2 py-2.5 text-center">
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.12em]">
                      {day}
                    </span>
                  </div>
                ))}
              </div>

              {/* Loading overlay */}
              {isLoading ? (
                <div className="grid grid-cols-7">
                  {Array.from({ length: 35 }).map((_, i) => (
                    <div
                      key={i}
                      className="min-h-[6rem] border-b border-r border-slate-200 p-1.5 animate-pulse"
                    >
                      <div className="w-5 h-5 bg-slate-100 rounded-full mb-1" />
                      <div className="h-3 bg-slate-100 rounded w-3/4 mb-1" />
                    </div>
                  ))}
                </div>
              ) : (
                /* Calendar cells */
                <div className="grid grid-cols-7">
                  {cells.map((day, index) => {
                    if (day === null) {
                      return <div key={`empty-${index}`} className="min-h-[6rem] border-b border-r border-slate-200 bg-slate-50/40" />
                    }

                    const dayEvents = getEventsForDay(day)
                    const dateStr = getDateString(day)
                    const isSelected = selectedDate === dateStr
                    const isTodayCell = isToday(day)

                    return (
                      <div
                        key={day}
                        role="button"
                        tabIndex={0}
                        aria-pressed={isSelected}
                        {...(isTodayCell ? { 'aria-current': 'date' as const } : {})}
                        onClick={() => setSelectedDate(isSelected ? null : dateStr)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setSelectedDate(isSelected ? null : dateStr)
                          }
                        }}
                        className={cn(
                          'min-h-[6rem] border-b border-r border-slate-200 p-1.5 cursor-pointer transition-colors',
                          'hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-inset',
                          isSelected && 'bg-primary-50 ring-1 ring-inset ring-primary-300',
                          (index + 1) % 7 === 0 && 'border-r-0'
                        )}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span
                            className={cn(
                              'text-xs font-medium tabular-nums w-5 h-5 flex items-center justify-center rounded-full',
                              isTodayCell
                                ? 'bg-primary-600 text-white text-[11px] font-semibold'
                                : 'text-slate-700'
                            )}
                          >
                            {day}
                          </span>
                        </div>
                        <div className="space-y-0.5">
                          {dayEvents.map((event) => {
                            const isContent = event.type === 'content'
                            const eventLabel = `${event.title}${event.client ? ` — ${event.client}` : ''}`
                            return isContent ? (
                              <button
                                key={event.id}
                                type="button"
                                disabled={loadingContentId === event.id}
                                aria-label={`Apri contenuto: ${eventLabel}`}
                                className={cn(
                                  'block w-full text-left text-[10px] font-medium px-1.5 py-0.5 rounded-md truncate leading-tight',
                                  'cursor-pointer hover:ring-1 hover:ring-primary-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary-400 disabled:opacity-60',
                                  getEventTimeColor(event.date)
                                )}
                                title={eventLabel}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  openContent(event.id)
                                }}
                              >
                                {event.title}
                              </button>
                            ) : (
                              <div
                                key={event.id}
                                className={cn(
                                  'text-[10px] font-medium px-1.5 py-0.5 rounded-md truncate leading-tight',
                                  getEventTimeColor(event.date)
                                )}
                                title={eventLabel}
                              >
                                {event.title}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Day detail panel */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-5">
          {selectedDate ? (
            <>
              <h3 className="text-sm font-semibold text-slate-900 mb-3 capitalize">
                {new Date(selectedDate + 'T12:00:00').toLocaleDateString('it-IT', {
                  weekday: 'long',
                  day: '2-digit',
                  month: 'long',
                })}
              </h3>
              {selectedDateEvents.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">
                  Nessun evento per questo giorno
                </p>
              ) : (
                <div className="space-y-2">
                  {selectedDateEvents.map((event) => {
                    const Icon = TYPE_ICONS[event.type]
                    const isContent = event.type === 'content'
                    const inner = (
                      <>
                        <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-medium leading-snug">{event.title}</p>
                          {event.client && (
                            <p className="text-[10px] opacity-70 mt-0.5 font-semibold">{event.client}</p>
                          )}
                          {event.project && (
                            <p className="text-[10px] opacity-70 mt-0.5">{event.project}</p>
                          )}
                          <p className="text-[10px] opacity-60 mt-0.5">{TYPE_LABELS[event.type]}</p>
                        </div>
                      </>
                    )
                    return isContent ? (
                      <button
                        key={event.id}
                        type="button"
                        disabled={loadingContentId === event.id}
                        aria-label={`Apri contenuto: ${event.title}`}
                        className={cn(
                          'flex w-full text-left items-start gap-2.5 p-2.5 rounded-lg',
                          'cursor-pointer hover:ring-1 hover:ring-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 disabled:opacity-60',
                          getEventTimeColor(event.date)
                        )}
                        onClick={() => openContent(event.id)}
                      >
                        {inner}
                      </button>
                    ) : (
                      <div
                        key={event.id}
                        className={cn(
                          'flex items-start gap-2.5 p-2.5 rounded-lg',
                          getEventTimeColor(event.date)
                        )}
                      >
                        {inner}
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-40 text-center">
              <CalendarIcon className="w-8 h-8 text-slate-200 mb-3" />
              <p className="text-sm text-slate-400">
                Seleziona un giorno per vedere gli eventi
              </p>
              {allEvents.length > 0 && (
                <p className="text-xs text-slate-400 mt-1 tabular-nums">
                  {allEvents.length} eventi questo mese
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content detail modal */}
      {selectedContent && (
        <ContentDetailModal
          content={selectedContent}
          onClose={() => setSelectedContent(null)}
          onStatusChange={async (id, newStatus) => {
            setSelectedContent((prev) => prev ? { ...prev, status: newStatus } as Content : null)
            await queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
          }}
          onUpdate={async (id, data) => {
            setSelectedContent((prev) => prev ? { ...prev, ...data } as Content : null)
            await queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
          }}
        />
      )}
    </div>
  )
}
